import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import submit from "submit-form";
import scriptLoader from "../../../../utils/scriptLoader";
import QuestionDisplay from "./QuestionDisplay";
import {
  getCheckInQuestions,
  getCheckInSchedule,
} from "../../../../utils/questionnaire";
import QuestionCreate from "./QuestionCreate";
import {
  validateCreateQuestion,
  validateCreateIndividualQuestion,
} from "../../../../utils/questionnaire-validations";
import { capitalize, set } from "lodash";
import ToolTip from "../../../../parts/tooltip-portal";

import moment from "moment";
import SvgIcon from "../../../../parts/SvgIcon";

const Questionnaire = (
  {
    athlete,
    origin,
    userToLoadInformation,
    organizationCoach,
    organizationOwner,
  },
  { locals }
) => {
  const { _id: athleteId } = athlete;
  const { assert, flash } = locals;
  const defaultTemplate =
    organizationOwner?.questionnaireTemplates?.defaultTemplate ||
    userToLoadInformation?.questionnaireTemplates?.defaultTemplate ||
    [];

  const referAsClients = locals.activeUser?.referAsClients || false;

  const [questions, setQuestions] = useState([]);

  const [orderedQuestions, setOrderedQuestions] = useState([]);

  const [changeOrder, setChangeOrder] = useState(false);

  const [currentQuestion, setCurrentQuestion] = useState({
    title: "",
    type: "",
    body: {},
  });

  const [save, setSave] = useState(false);
  const [edit, setEdit] = useState(false);
  const [addNew, setAddNew] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editedQuestion, setEditedQuestion] = useState({});
  const [schedule, setSchedule] = useState(null);
  const [refresh, setRefresh] = useState(false);
  useEffect(() => {
    const athleteQuestionnaire = getCheckInQuestions(
      organizationOwner || userToLoadInformation,
      athlete
    );
    const athleteSchedule = getCheckInSchedule(athlete);
    setQuestions(athleteQuestionnaire);
    setSchedule(athleteSchedule);
  }, [userToLoadInformation, athlete]);

  useEffect(() => {
    if (document.querySelector(".principal-grabber-div")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@latest/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          const sortable = new Sortable(
            document.querySelector(".principal-grabber-div"),
            {
              animation: 350,
              handle: ".drag-handle",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                const newOrderedQuestions = Array.from(
                  document.querySelectorAll(".item-grabber-container")
                ).map((question) => question.id);

                setOrderedQuestions(newOrderedQuestions);
              },
            }
          );
        }
      );
    }
  }, []);

  /**
   * Save reorder
   */
  const saveReorder = () => {
    if (orderedQuestions.length > 0) {
      let orderedItems = [...questions];
      orderedItems.sort((a, b) => {
        const indexA = orderedQuestions.indexOf(a._id);
        const indexB = orderedQuestions.indexOf(b._id);
        return indexA - indexB;
      });

      submit("/actions/questionnaire/save-athlete-changes", {
        method: "POST",
        body: {
          questions: orderedItems,
          athleteId,
          // no need to pass the organization owner since it only works with
          // the coach's athlete information (not templates or whatever)
          userToRequest: userToLoadInformation._id,
        },
      });
    }

    setChangeOrder(false);
  };

  /**
   * Cancel reorder
   */
  const cancelReorder = () => {
    // window.location.href = organizationCoach
    //   ? `${origin}/athlete-portal/profile?section=questionnaire&athlete=${athlete._id}&organizationCoach=${organizationCoach}`
    //   : `${origin}/athlete-portal/profile?section=questionnaire&athlete=${athlete._id}`;
    setChangeOrder(false);
    setOrderedQuestions([]);
    setRefresh(!refresh);
  };

  /**
   * Edit question
   */
  const onEdit = (id) => {
    const question = questions.find((question) => question._id === id);
    setEditedQuestion(question);
    setEdit(true);
    setSave(true);
    setAddNew(false);
  };

  const handleSaveQuestion = () => {
    const isEditedValid = edit
      ? validateCreateQuestion(editedQuestion, assert, flash)
      : true;
    const isCreateValid = adding
      ? validateCreateQuestion(currentQuestion, assert, flash)
      : true;

    if (isCreateValid && isEditedValid) {
      const newQuestion = {
        ...currentQuestion,
        _id: `${currentQuestion.title}-${questions.length}`,
      };

      const updatedQuestions = edit
        ? getEditedQuestions(editedQuestion, newQuestion, adding)
        : [...questions, newQuestion];

      const newQuestions = updatedQuestions.map((question) => ({
        title: question.title,
        type: question.type,
        body: { ...question.body },
      }));

      if (validateCreateIndividualQuestion(newQuestions, assert, flash)) {
        setQuestions(updatedQuestions);

        submit("/actions/questionnaire/save-athlete-changes", {
          method: "POST",
          body: {
            questions: newQuestions,
            athleteId,
            // no need to pass the organization owner since it only works with
            // the coach's athlete information (not templates or whatever)
            userToRequest: userToLoadInformation._id,
          },
        });

        setEditedQuestion({});
        setCurrentQuestion({ title: "", type: "", body: {} });
        setSave(false);
        setEdit(false);
        setAdding(false);
        setAddNew(true);
      }
    }
  };

  const handleCancel = () => {
    setEditedQuestion({});
    setCurrentQuestion({ title: "", type: "", body: {} });
    setSave(false);
    setEdit(false);
    setAdding(false);
    setAddNew(true);
  };

  const getEditedQuestions = (editedQuestion, newQuestion, isAdding) => {
    const editedQuestions = questions.map((question) =>
      question._id === editedQuestion._id ? editedQuestion : question
    );

    const result = isAdding
      ? [...editedQuestions, { ...newQuestion }]
      : editedQuestions;

    return result;
  };

  const onDelete = (id) => {
    if (confirm("Are you sure you want to delete this question?")) {
      const updatedQuestions = questions.filter(
        (question) => question._id !== id
      );

      setQuestions(updatedQuestions);

      submit("/actions/questionnaire/save-athlete-changes", {
        method: "POST",
        body: {
          questions: updatedQuestions,
          athleteId,
          isDeleting: true,
          defaultTemplate,
          // no need to pass the organization owner since it only works with
          // the coach's athlete information (not templates or whatever)
          userToRequest: userToLoadInformation._id,
        },
      });
    }
  };

  const handleAddNew = () => {
    setAddNew(false);
    setAdding(true);
    setSave(true);
  };

  return (
    <div role="questionnaire-wrapper">
      <header>
        <div>
          <div className="tooltip">
            <h4>Check-In Questionnaire</h4>
            <ToolTip
              id={"check-in-tooltip"}
              body={
                <>
                  Visit{" "}
                  <a href="/coaching-portal/msb-university">MSB University</a>{" "}
                  for a tutorial video on this feature
                </>
              }
              childrenContainerClass="tooltip-coach"
              timeOut={1000}
              position="top"
            >
              <SvgIcon
                alt="info icon"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
          <p className="p5">
            Create a scheduled check-in questionnaire for your{" "}
            {referAsClients ? "client's" : "athlete's"} to complete to help you
            collect more data
          </p>

          {!!questions.length && (
            <ScheduleValidationDisplay
              schedule={schedule}
              questions={questions}
              classNames="force-mobile-hidden"
            />
          )}
        </div>
        <HgroupDisplay
          changeOrder={changeOrder}
          edit={edit}
          adding={adding}
          setChangeOrder={setChangeOrder}
          saveReorder={saveReorder}
          cancelReorder={cancelReorder}
          athleteId={athleteId}
          organizationCoach={organizationCoach}
          questions={questions}
        />
        {!!questions.length && (
          <ScheduleValidationDisplay
            schedule={schedule}
            questions={questions}
            classNames="force-only-show-mobile"
          />
        )}
      </header>
      <div className="questionnaire-body">
        <div className="principal-grabber-div">
          {!!questions.length &&
            questions.map((question, index) => (
              <div
                className="item-grabber-container"
                id={question._id}
                key={`${question._id}${refresh}`}
              >
                {question._id === editedQuestion._id && (
                  <QuestionCreate
                    data={editedQuestion}
                    setData={setEditedQuestion}
                    isEdited={true}
                    key={`${question._id}`}
                  />
                )}

                {question._id !== editedQuestion._id && (
                  <QuestionDisplay
                    key={`${question.title}-${index}`}
                    body={question.body}
                    title={question.title}
                    id={question._id}
                    type={question.type}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    disableEdit={changeOrder}
                  />
                )}
                {changeOrder && (
                  <div className="drag-handle">
                    <SvgIcon src="/img/new-icons/menu.svg" alt="grab" />
                  </div>
                )}
              </div>
            ))}
          {adding && (
            <div className="questionnaire-question-container">
              <QuestionCreate
                data={currentQuestion}
                setData={setCurrentQuestion}
              />
            </div>
          )}
          {!questions.length && !adding && (
            <section className="no-questions">
              <h4>No check-in questionnaire assigned</h4>
              <p className="p5">
                Create or assign a template to schedule a check-in
              </p>

              <div>
                <a
                  role="button"
                  data-button-type="primary"
                  href="/actions/refresh?modal[type]=questionnaire-templates"
                >
                  Assign Template
                </a>
                <button data-button-type="secondary" onClick={handleAddNew}>
                  Create New
                </button>
              </div>
            </section>
          )}
        </div>
      </div>

      {(!!questions.length || adding) && (
        <>
          {/* ----------- FOOTER FOR DESKTOP---------- */}
          <footer className="force-mobile-hidden">
            {addNew && (
              <button data-button-type="outline" onClick={handleAddNew}>
                Add New
              </button>
            )}
            {save && (
              <AsideActionsButtons
                handleCancel={handleCancel}
                handleSaveQuestion={handleSaveQuestion}
              />
            )}
            {!addNew ||
              (!save && (
                <aside>
                  <a
                    data-button-type="primary"
                    role="button"
                    href="/actions/refresh?modal[type]=questionnaire-schedule"
                  >
                    {schedule ? "Change Schedule" : "Schedule"}
                  </a>
                  {schedule && (
                    <ScheduleDisplay
                      className={"footer-schedule"}
                      schedule={schedule}
                    />
                  )}
                </aside>
              ))}
          </footer>
          {/* ----------- FOOTER FOR MOBILE---------- */}
          <footer className="force-only-show-mobile">
            {addNew && (
              <div>
                <button data-button-type="outline" onClick={handleAddNew}>
                  Add New
                </button>
                {!addNew ||
                  (!save && (
                    <a
                      data-button-type="primary"
                      role="button"
                      href="/actions/refresh?modal[type]=questionnaire-schedule"
                    >
                      {schedule ? "Change Schedule" : "Schedule"}
                    </a>
                  ))}
              </div>
            )}
            {save && (
              <AsideActionsButtons
                handleCancel={handleCancel}
                handleSaveQuestion={handleSaveQuestion}
              />
            )}
            {!addNew ||
              (!save && schedule && (
                <ScheduleDisplay
                  className={"footer-schedule"}
                  schedule={schedule}
                />
              ))}
          </footer>
        </>
      )}
    </div>
  );
};

const ScheduleValidationDisplay = ({
  schedule,
  questions,
  classNames = "",
}) => {
  return (
    <div className={classNames}>
      {schedule ? (
        <ScheduleDisplay schedule={schedule} />
      ) : (
        questions.length > 0 && (
          <div className="chip warning">
            This questionnaire has not been scheduled yet
          </div>
        )
      )}
    </div>
  );
};

const AsideActionsButtons = ({ handleCancel, handleSaveQuestion }) => {
  return (
    <aside>
      <button
        data-button-type="secondary"
        data-action="cancel"
        onClick={handleCancel}
      >
        Cancel
      </button>
      <button
        data-button-type="primary"
        role="button"
        onClick={handleSaveQuestion}
      >
        Save
      </button>
    </aside>
  );
};

const HgroupDisplay = ({
  changeOrder,
  edit,
  adding,
  setChangeOrder,
  saveReorder,
  cancelReorder,
  athleteId,
  organizationCoach,
  questions,
}) => {
  return (
    <hgroup>
      {!changeOrder && !!questions.length && (
        <button
          data-button-type="secondary"
          onClick={() => {
            setChangeOrder(!changeOrder);
          }}
          disabled={changeOrder || edit || adding}
        >
          Change Order
        </button>
      )}
      {changeOrder && (
        <button onClick={saveReorder} data-button-type="primary">
          Save
        </button>
      )}
      {changeOrder && (
        <button
          onClick={cancelReorder}
          data-button-type="secondary"
          data-action="cancel"
        >
          Cancel
        </button>
      )}
      {!changeOrder && (
        <a
          className={!questions.length ? "force-w-full" : ""}
          role="button"
          data-button-type="secondary"
          href="/actions/refresh?modal[type]=questionnaire-templates"
        >
          Template
        </a>
      )}
      {!changeOrder && !!questions.length && (
        <a
          className="mobile-hidden" // Hidding for mobile cause responsive is not yet ready
          data-button-type="primary"
          role="button"
          href={
            organizationCoach
              ? `/athlete-portal/metrics/check-in-summary?date=${moment().format(
                  "YYYYMMDD"
                )}&athlete=${athleteId}&organizationCoach=${organizationCoach}`
              : `/athlete-portal/metrics/check-in-summary?date=${moment().format(
                  "YYYYMMDD"
                )}&athlete=${athleteId}`
          }
        >
          View Report
        </a>
      )}
    </hgroup>
  );
};

const ScheduleDisplay = ({ schedule }) => {
  if (schedule.frequency === "daily") {
    const { selectedWeeklyDays } = schedule;

    const days = [];
    for (const day in selectedWeeklyDays) {
      if (selectedWeeklyDays[day].active === true) {
        days.push(capitalize(selectedWeeklyDays[day].day));
      }
    }

    return (
      <div className="chip success">
        Schedule - {days.join(", ")} | Every {getFrequency(schedule.frequency)}
      </div>
    );
  }

  return (
    <div className="chip success">
      Schedule - {capitalize(schedule.dayOfWeek.slice(0, 3))} | Every{" "}
      {getFrequency(schedule.frequency)}
    </div>
  );
};

const getFrequency = (frequency) => {
  const map = {
    daily: "selected day",
    weekly: "week",
    biweekly: "two weeks",
    monthly: "month",
  };

  return map[frequency];
};

Questionnaire.contextTypes = { locals: PropTypes.object };

module.exports = Questionnaire;
